import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  CreditCard, 
  Download, 
  FileText, 
  Loader2, 
  MoreHorizontal, 
  RefreshCw, 
  Send,
  Clock,
  Receipt,
  Building,
  User,
  CalendarClock,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/common/PageHeader";
import { StatusBadge } from '@/components/common/StatusBadge';

interface InvoiceDetail {
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  paid_date: string | null;
  status: string;
  currency: string;
  total_amount: number;
  items: {
    id: string;
    amount_due: number;
    subscription_item: {
      subscription_id: string;
      subscription_plan: {
        name: string;
      };
      quantity: number;
      unit_price: number;
    };
  }[];
  subscription: {
    id: string;
    customer_relationship: {
      id: string;
      relationship?: string;
      customer: {
        id: string;
        name: string;
        legal_name: string;
      };
    };
  };
}

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [isMarkingAsPaidDialogOpen, setIsMarkingAsPaidDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [paymentReference, setPaymentReference] = useState('');

  useEffect(() => {
    if (id) {
      fetchInvoiceDetails(id);
    } else {
      setLoading(false);
      toast.error('Número da fatura não fornecido');
    }
  }, [id]);

  const fetchInvoiceDetails = async (invoiceId: string) => {
    setLoading(true);
    try {
      console.log('Buscando detalhes da fatura:', invoiceId);
      
      // Buscar a fatura principal
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .select(`
          id,
          subscription_id,
          invoice_number,
          invoice_date,
          amount_due,
          currency,
          due_date,
          paid_date,
          status
        `)
        .eq('invoice_number', invoiceId)
        .single();
      
      if (invoiceError) {
        console.error('Erro ao buscar fatura:', invoiceError);
        throw invoiceError;
      }
      
      if (!invoiceData) {
        console.error('Fatura não encontrada:', invoiceId);
        throw new Error('Fatura não encontrada');
      }
      
      console.log('Fatura encontrada:', invoiceData);
      
      // Buscar os itens da fatura
      const { data: invoiceItems, error: invoiceItemsError } = await supabase
        .from('invoice_items')
        .select(`
          id,
          quantity,
          unit_price,
          amount_price,
          subscription_plan_id,
          subscription_plan:subscription_plan_id (
            id,
            name
          )
        `)
        .eq('invoice_id', invoiceData.id);
      
      if (invoiceItemsError) {
        console.error('Erro ao buscar itens da fatura:', invoiceItemsError);
        throw invoiceItemsError;
      }
      
      console.log('Itens da fatura encontrados:', invoiceItems);
      
      // Buscar informações da assinatura e do cliente
      const subscriptionId = invoiceData.subscription_id;
      
      if (!subscriptionId) {
        throw new Error('ID da assinatura não encontrado');
      }
      
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select(`
          id,
          customer_relationship:customer_relationship_id (
            id,
            relationship,
            customer:customer_id (
              id,
              name,
              legal_name
            )
          )
        `)
        .eq('id', subscriptionId)
        .single();
      
      if (subscriptionError) {
        console.error('Erro ao buscar dados da assinatura:', subscriptionError);
        throw subscriptionError;
      }
      
      console.log('Dados da assinatura:', subscriptionData);
      console.log('Estrutura customer_relationship:', JSON.stringify(subscriptionData.customer_relationship, null, 2));
      
      // Construir o objeto de detalhes da fatura
      const customerRelationship = (subscriptionData.customer_relationship as any)?.[0] || {};
      const customer = (customerRelationship.customer as any)?.[0] || {};
      
      const invoiceDetail: InvoiceDetail = {
        invoice_number: invoiceData.invoice_number,
        invoice_date: invoiceData.invoice_date,
        due_date: invoiceData.due_date,
        paid_date: invoiceData.paid_date,
        status: invoiceData.status,
        currency: invoiceData.currency,
        total_amount: invoiceData.amount_due,
        items: invoiceItems.map(item => ({
          id: item.id,
          amount_due: item.amount_price || 0,
          subscription_item: {
            subscription_id: invoiceData.subscription_id,
            subscription_plan: {
              name: (item.subscription_plan as any)?.name || 'Plano sem nome'
            },
            quantity: item.quantity || 0,
            unit_price: item.unit_price || 0
          }
        })),
        subscription: {
          id: subscriptionData.id,
          customer_relationship: {
            id: customerRelationship.id || '',
            relationship: customerRelationship.relationship,
            customer: {
              id: customer.id || '',
              name: customer.name || '',
              legal_name: customer.legal_name || ''
            }
          }
        }
      };
      
      setInvoice(invoiceDetail);
    } catch (error) {
      console.error('Erro ao buscar detalhes da fatura:', error);
      toast.error('Não foi possível carregar os detalhes da fatura');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!invoice) return;
    
    setIsMarkingAsPaid(true);
    
    try {
      // Atualizar a fatura principal para status "paid"
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'paid',
          paid_date: new Date(paymentDate).toISOString(),
          updated_by: 'user'
        })
        .eq('invoice_number', invoice.invoice_number);
      
      if (error) {
        throw error;
      }
      
      // Registrar a operação na tabela de orders
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: invoice.subscription.id,
          operation_type: 'pagamento',
          quantity_change: null,
          operation_date: new Date().toISOString(),
        });
      
      if (orderError) {
        throw orderError;
      }
      
      toast.success('Fatura marcada como paga com sucesso');
      
      // Atualizar os dados da fatura
      await fetchInvoiceDetails(invoice.invoice_number);
      
      // Fechar o modal
      setIsMarkingAsPaidDialogOpen(false);
    } catch (error) {
      console.error('Erro ao marcar fatura como paga:', error);
      toast.error('Não foi possível marcar a fatura como paga');
    } finally {
      setIsMarkingAsPaid(false);
    }
  };

  const handleCancelInvoice = async () => {
    if (!invoice) return;
    
    if (!confirm('Tem certeza que deseja cancelar esta fatura? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      // Atualizar a fatura principal para status "canceled"
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'canceled',
          updated_by: 'user'
        })
        .eq('invoice_number', invoice.invoice_number);
      
      if (error) {
        throw error;
      }
      
      // Registrar a operação na tabela de orders
      const { error: orderError } = await supabase
        .from('orders')
        .insert({
          subscription_id: invoice.subscription.id,
          operation_type: 'cancelamento_fatura',
          quantity_change: null,
          operation_date: new Date().toISOString(),
        });
      
      if (orderError) {
        throw orderError;
      }
      
      toast.success('Fatura cancelada com sucesso');
      
      // Atualizar os dados da fatura
      await fetchInvoiceDetails(invoice.invoice_number);
    } catch (error) {
      console.error('Erro ao cancelar fatura:', error);
      toast.error('Não foi possível cancelar a fatura');
    }
  };

  const handleSendInvoice = () => {
    toast.info('Funcionalidade de envio de fatura será implementada em breve');
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const handleDownloadInvoice = () => {
    toast.info('Funcionalidade de download de fatura será implementada em breve');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="w-full px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <h2 className="mt-4 text-xl font-extrabold">Carregando detalhes da fatura...</h2>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!invoice) {
    return (
      <DashboardLayout>
        <div className="w-full px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-destructive text-xl">
                <h2 className="font-extrabold">Erro</h2>
                <p className="mt-2">Fatura não encontrada</p>
              </div>
              <Button className="mt-6" onClick={() => navigate(-1)}>
                Voltar
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full px-6 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Fatura #{invoice.invoice_number}</h1>
              <p className="text-muted-foreground">
                Cliente: {invoice.subscription.customer_relationship.customer.name}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => navigate(`/subscriptions/${invoice.subscription.id}`)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Gerenciar Fatura</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {invoice.status.toLowerCase() === 'pending' && (
                    <DropdownMenuItem onClick={() => setIsMarkingAsPaidDialogOpen(true)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Marcar como Paga
                    </DropdownMenuItem>
                  )}
                  {invoice.status.toLowerCase() === 'pending' && (
                    <DropdownMenuItem onClick={handleCancelInvoice}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancelar Fatura
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleSendInvoice}>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar por E-mail
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleDownloadInvoice}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Status Card */}
          <Card className={`mt-6 mb-6 ${
            invoice.status.toLowerCase() === 'paid' ? 'border-green-500 bg-green-50' : 
            invoice.status.toLowerCase() === 'pending' ? 'border-yellow-500 bg-yellow-50' : 
            invoice.status.toLowerCase() === 'overdue' ? 'border-orange-500 bg-orange-50' : 
            invoice.status.toLowerCase() === 'canceled' ? 'border-red-500 bg-red-50' : ''
          }`}>
            <CardContent className="p-6">
              <div className="flex flex-wrap gap-6 justify-between">
                <div className="flex items-center gap-2">
                  <div className="font-extrabold text-muted-foreground">Status:</div>
                  <div className="min-w-[112px]"><StatusBadge status={invoice.status} context="invoice" /></div>
                  {invoice.paid_date && (
                    <div className="text-sm text-muted-foreground ml-2">
                      Pago em: {formatDate(invoice.paid_date)}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div className="font-extrabold">
                    Emissão: {formatDate(invoice.invoice_date)}
                    {invoice.due_date && ` | Vencimento: ${formatDate(invoice.due_date)}`}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <div className="font-extrabold">
                    Total: {formatCurrency(invoice.total_amount, invoice.currency)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Cliente e Assinatura */}
            <Card className="md:col-span-1">
              <CardHeader>
                <CardTitle>Informações</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-extrabold flex items-center gap-2 mb-2">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      Cliente
                    </h3>
                    <div className="text-sm">
                      <div className="font-extrabold">{invoice.subscription.customer_relationship.customer.name}</div>
                      <div className="text-muted-foreground">{invoice.subscription.customer_relationship.customer.legal_name}</div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-sm font-extrabold flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      Tenant
                    </h3>
                    <div className="text-sm">
                      <div>{invoice.subscription.customer_relationship.relationship}</div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-sm font-extrabold flex items-center gap-2 mb-2">
                      <Receipt className="h-4 w-4 text-muted-foreground" />
                      Assinatura
                    </h3>
                    <div className="text-sm">
                      <div>ID: {invoice.subscription.id}</div>
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-sm"
                        onClick={() => navigate(`/subscriptions/${invoice.subscription.id}`)}
                      >
                        Ver Assinatura
                      </Button>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-sm font-extrabold flex items-center gap-2 mb-2">
                      <CalendarClock className="h-4 w-4 text-muted-foreground" />
                      Datas
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-muted-foreground">Emissão:</div>
                      <div>{formatDate(invoice.invoice_date)}</div>
                      
                      <div className="text-muted-foreground">Vencimento:</div>
                      <div>{invoice.due_date ? formatDate(invoice.due_date) : '-'}</div>
                      
                      <div className="text-muted-foreground">Pagamento:</div>
                      <div>{invoice.paid_date ? formatDate(invoice.paid_date) : '-'}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Itens da Fatura */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Itens da Fatura</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plano</TableHead>
                      <TableHead className="text-center">Quantidade</TableHead>
                      <TableHead>Preço Unitário</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-extrabold">{item.subscription_item.subscription_plan.name}</TableCell>
                        <TableCell className="text-center">{item.subscription_item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.subscription_item.unit_price, invoice.currency)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.amount_due, invoice.currency)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-extrabold">Total:</TableCell>
                      <TableCell className="text-right font-extrabold">{formatCurrency(invoice.total_amount, invoice.currency)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Modal para marcar como pago */}
          <Dialog open={isMarkingAsPaidDialogOpen} onOpenChange={setIsMarkingAsPaidDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Marcar Fatura como Paga
                </DialogTitle>
                <DialogDescription>
                  Informe a data de pagamento e referência para marcar esta fatura como paga.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="payment-date">Data de Pagamento</Label>
                  <Input 
                    id="payment-date" 
                    type="date" 
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="payment-reference">Referência de Pagamento (opcional)</Label>
                  <Input 
                    id="payment-reference" 
                    placeholder="Ex: Transação #12345"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                  />
                </div>
                
                <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-extrabold text-blue-800">Informações</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Ao marcar esta fatura como paga, o status será atualizado e a data de pagamento será registrada.
                        Esta ação será registrada no histórico de operações da assinatura.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsMarkingAsPaidDialogOpen(false)}
                  disabled={isMarkingAsPaid}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleMarkAsPaid}
                  disabled={isMarkingAsPaid || !paymentDate}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isMarkingAsPaid ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processando
                    </>
                  ) : (
                    'Confirmar Pagamento'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InvoiceDetail; 